// Dynamic Bar Chart Component - Handles any data structure

import React, { useMemo, useState, useEffect } from 'react';
import { BarChart3 } from 'lucide-react';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList, Cell } from 'recharts';
import CustomTooltip from '../common/CustomTooltip';
import { COLORS, CHART_COLORS } from '../../constants/theme';
import { truncateLabel, formatNumber, getOptimalLabelLength } from '../../utils/chartUtils';

// Hook to track window size
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};


// Custom label component for bars - receives isMobile as prop
const CustomBarLabel = ({ x, y, width, value, color, isMobile, fontSize }) => {
  if (!value || value === 0 || !width) return null;
  const displayValue = formatNumber(value);
  const labelHeight = 20;
  const isTop = y > labelHeight + 5;
  
  // Hide labels on mobile if too small
  if (isMobile && width < 25) return null;
  
  return (
    <text
      x={x + width / 2}
      y={isTop ? y - 5 : y + width + 15}
      fill={isTop ? color : '#374151'}
      fontSize={isMobile ? 11 : 12}
      fontWeight={600}
      textAnchor="middle"
      style={{ pointerEvents: 'none' }}
    >
      {displayValue}
    </text>
  );
};

const BarChart = ({ data }) => {
  const [showDataLabels] = useState(false);
  const [hoveredBar, setHoveredBar] = useState(null);
  const { width: windowWidth } = useWindowSize();
  
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;
  const chartHeight = isMobile ? 400 : isTablet ? 480 : 550;
  const fontSize = isMobile ? 13 : isTablet ? 14 : 15;
  const yAxisWidth = isMobile ? 60 : isTablet ? 75 : 90;

  // Build a count-by-category aggregation for the best categorical column
  const buildCountChart = (rows, excludeKey) => {
    const keys = Object.keys(rows[0]);
    // Pick the categorical column with lowest cardinality (but >1 unique) that isn't the exclude key
    const PREFERRED = ['status', 'type', 'market', 'country', 'priority', 'category', 'region'];
    const candidates = keys.filter(k => {
      if (k === excludeKey || k === 'id') return false;
      const uniques = new Set(rows.map(r => r[k]).filter(v => v != null && String(v).trim() !== '')).size;
      return uniques >= 2 && uniques <= Math.max(rows.length, 10);
    });
    // Sort: prefer known names, then by lowest cardinality
    candidates.sort((a, b) => {
      const ai = PREFERRED.findIndex(p => a.toLowerCase().includes(p));
      const bi = PREFERRED.findIndex(p => b.toLowerCase().includes(p));
      if (ai !== -1 && bi === -1) return -1;
      if (bi !== -1 && ai === -1) return 1;
      const ua = new Set(rows.map(r => r[a])).size;
      const ub = new Set(rows.map(r => r[b])).size;
      return ua - ub;
    });
    const groupKey = candidates[0] || keys.find(k => k !== excludeKey) || keys[0];
    const counts = {};
    rows.forEach(row => {
      const val = String(row[groupKey] ?? 'Unknown').trim() || 'Unknown';
      counts[val] = (counts[val] || 0) + 1;
    });
    const aggregated = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([label, count]) => ({ [groupKey]: label, Count: count }));
    return { xAxisKey: groupKey, yAxisKeys: ['Count'], colors: [COLORS.primary], isCountChart: true, groupKey, aggregated };
  };

  // Dynamically detect columns from data
  const chartConfig = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;
    const firstRow = data[0];
    if (!firstRow || typeof firstRow !== 'object') return null;
    const keys = Object.keys(firstRow);
    if (keys.length === 0) return null;

    // Find the best string column for X-axis
    const xAxisKey = keys.find(key => {
      const value = firstRow[key];
      if (value === null || value === undefined) return false;
      return typeof value === 'string' && String(value).length < 50;
    }) || keys[0];

    // Find numeric columns
    const numericKeys = keys.filter(key => {
      if (key === xAxisKey) return false;
      const values = data.map(row => {
        const v = row[key];
        if (v === null || v === undefined) return null;
        if (typeof v === 'number') return v;
        if (typeof v === 'string' && v.trim() !== '') {
          const p = Number(v.replace(/,/g, '').trim());
          return Number.isNaN(p) ? null : p;
        }
        return null;
      }).filter(v => typeof v === 'number');
      return values.length > 0;
    });

    // If no numeric columns found → build a count chart
    if (numericKeys.length === 0) {
      return buildCountChart(data, null);
    }

    const ranked = numericKeys.map(key => {
      const values = data.map(row => {
        const v = row[key];
        if (v === null || v === undefined) return null;
        if (typeof v === 'number') return v;
        if (typeof v === 'string') { const p = Number(v.replace(/,/g, '').trim()); return isNaN(p) ? null : p; }
        return null;
      }).filter(v => typeof v === 'number');
      if (!values.length) return { key, variance: 0, max: 0 };
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const variance = values.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / values.length;
      const max = Math.max(...values);
      return { key, variance, max };
    }).sort((a, b) => b.variance - a.variance);

    // If all numeric values are effectively zero → fall back to count chart
    const hasRealValues = ranked.some(r => r.max > 0);
    if (!hasRealValues) {
      return buildCountChart(data, null);
    }

    const yAxisKeys = ranked.slice(0, 3).map(r => r.key);
    return { xAxisKey, yAxisKeys, colors: CHART_COLORS.slice(0, yAxisKeys.length), isCountChart: false };
  }, [data]);

  // Transform data to ensure numeric values
  const transformedData = useMemo(() => {
    if (!data || !Array.isArray(data) || !chartConfig) return [];
    // Count charts already have aggregated data
    if (chartConfig.isCountChart) return chartConfig.aggregated;

    return data.map((row, idx) => {
      const transformed = { ...row };
      if (chartConfig.xAxisKey && transformed[chartConfig.xAxisKey] !== undefined) {
        transformed[chartConfig.xAxisKey] = String(transformed[chartConfig.xAxisKey] || `Item ${idx + 1}`);
      }
      chartConfig.yAxisKeys.forEach(key => {
        const num = Number(transformed[key]);
        transformed[key] = isNaN(num) ? 0 : num;
      });
      return transformed;
    });
  }, [data, chartConfig]);

  // Calculate max value for Y-axis domain
  const maxValue = useMemo(() => {
    if (!transformedData.length || !chartConfig) return 100;
    let max = 0;
    transformedData.forEach(row => {
      chartConfig.yAxisKeys.forEach(key => {
        if (row[key] > max) max = row[key];
      });
    });
    return max * 1.15; // Add 15% padding for labels
  }, [transformedData, chartConfig]);

  const summaryStats = useMemo(() => {
    if (!chartConfig || !transformedData.length) return null;
    const primaryKey = chartConfig.yAxisKeys[0];
    if (!primaryKey) return null;
    const sorted = [...transformedData]
      .sort((a, b) => (Number(b[primaryKey]) || 0) - (Number(a[primaryKey]) || 0))
      .slice(0, 3)
      .map((row) => ({
        label: row[chartConfig.xAxisKey],
        value: Number(row[primaryKey]) || 0,
      }));
    return { primaryKey, sorted };
  }, [chartConfig, transformedData]);

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center border border-gray-200">
        <p className="text-gray-500">No data available for chart visualization.</p>
      </div>
    );
  }

  if (!chartConfig || chartConfig.yAxisKeys.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center border border-gray-200">
        <p className="text-gray-500">No numeric data available for bar chart.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in-up">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 animate-fade-in-down">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 animate-float flex-shrink-0" style={{ color: COLORS.primary }} />
            <span className="truncate">
              {chartConfig.isCountChart
                ? `Distribution by ${chartConfig.groupKey}`
                : 'Data Comparison'}
            </span>
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 animate-fade-in">
            {chartConfig.isCountChart
              ? `${transformedData.length} categories across ${data.length} records`
              : `${data.length} item${data.length !== 1 ? 's' : ''} across ${chartConfig.yAxisKeys.length} metric${chartConfig.yAxisKeys.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        {summaryStats && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            <p className="text-slate-500 mb-1">Top 3 by {summaryStats.primaryKey}</p>
            <p className="font-semibold text-slate-800">
              {summaryStats.sorted.map((item) => `${item.label}: ${formatNumber(item.value)}`).join(' | ')}
            </p>
          </div>
        )}
      </div>
      <div className="bg-white rounded-lg p-4 sm:p-5 md:p-6 border border-gray-200 shadow-sm chart-animate card-hover">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <RechartsBarChart 
            data={transformedData}
              margin={{ 
                top: 25, 
                right: isMobile ? 15 : isTablet ? 35 : 50, 
                left: isMobile ? 55 : isTablet ? 70 : 80, 
                bottom: transformedData.length > 8 ? (isMobile ? 110 : isTablet ? 130 : 150) : (isMobile ? 70 : isTablet ? 90 : 100) 
              }}
          >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" opacity={0.5} />
              <XAxis 
                dataKey={chartConfig.xAxisKey} 
                stroke={COLORS.secondary}
                angle={transformedData.length > 6 ? -45 : 0}
                textAnchor={transformedData.length > 6 ? 'end' : 'middle'}
                height={transformedData.length > 6 ? (isMobile ? 130 : isTablet ? 125 : 120) : (isMobile ? 70 : isTablet ? 65 : 70)}
                tick={{ fill: '#374151', fontSize: fontSize }}
                style={{ fontSize: `${fontSize}px`, fontWeight: '600' }}
                interval={isMobile && transformedData.length > 8 ? 'preserveStartEnd' : 0}
                minTickGap={isMobile ? 5 : isTablet ? 6 : 8}
                tickFormatter={(value) => {
                  const isRotated = transformedData.length > 6;
                  const maxLength = getOptimalLabelLength(isMobile, isTablet, transformedData.length, isRotated);
                  return truncateLabel(value, maxLength);
                }}
              />
              <YAxis 
                stroke={COLORS.secondary}
                tick={{ fill: '#374151', fontSize: fontSize }}
                style={{ fontSize: `${fontSize}px`, fontWeight: '600' }}
                tickFormatter={formatNumber}
                domain={[0, maxValue]}
                width={yAxisWidth}
                label={{ value: '', angle: -90, position: 'insideLeft' }}
              />
            <Tooltip 
              content={<CustomTooltip />}
              cursor={{ fill: 'rgba(10, 61, 98, 0.1)', stroke: COLORS.primary, strokeWidth: 2 }}
              animationDuration={300}
              animationEasing="ease-out"
            />
            <Legend 
              wrapperStyle={{ 
                fontSize: `${fontSize}px`, 
                fontWeight: '600', 
                paddingTop: '15px',
                paddingBottom: '5px'
              }}
              iconType="rect"
              iconSize={isMobile ? 14 : isDesktop ? 18 : 16}
            />
            {chartConfig.yAxisKeys.map((key, idx) => (
              <Bar 
                key={key}
                dataKey={key} 
                fill={chartConfig.colors[idx % chartConfig.colors.length]} 
                radius={[4, 4, 0, 0]} 
                name={key}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredBar(key)}
                onMouseLeave={() => setHoveredBar(null)}
                onClick={(data) => {
                  console.log('Bar clicked:', data);
                }}
              >
                {showDataLabels && (
                  <LabelList 
                    content={(props) => (
                      <CustomBarLabel 
                        x={props.x} 
                        y={props.y} 
                        width={props.width} 
                        value={props.value} 
                        color={chartConfig.colors[idx % chartConfig.colors.length]}
                        isMobile={isMobile}
                        fontSize={fontSize}
                      />
                    )}
                  />
                )}
                {transformedData.map((entry, index) => {
                  const isHovered = hoveredBar === key;
                  const opacity = hoveredBar === null || isHovered ? 1 : 0.5;
                  return (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={chartConfig.colors[idx % chartConfig.colors.length]}
                      opacity={opacity}
                      style={{ 
                        transition: 'opacity 0.2s ease',
                        filter: isHovered ? 'brightness(1.1)' : 'none'
                      }}
                    />
                  );
                })}
              </Bar>
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChart;
